import { ConnectStoresDialog } from "@/components/connect-stores-dialog";
import { OneOffItemsSetting } from "@/components/dashboard/one-off-items-setting";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input, ReadOnlyInput } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { dashboardPath } from "@/constants";
import i18n from "@/i18n";
import { getCurrentUser } from "@/hooks/user";
import { authedFetch } from "@/lib/api-client";
import type { ErrorResponse } from "@/lib/types";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

async function updateOrganization(
  token: string,
  orgName: string,
  currency: string,
  orgId: string,
) {
  const resp = await authedFetch(`/v1/organizations/${orgId}`, {
    method: "POST",
    token,
    orgId,
    body: JSON.stringify({
      name: orgName,
      currency: currency,
    }),
  });

  if (resp.status > 399) {
    const errorData = await resp.json();
    throw { status: resp.status, data: errorData };
  }

  return resp;
}

export const Route = createFileRoute("/$org/settings/organization")({
  component: OrganizationComponent,
  loader: async ({ context }) => {
    const { getToken, setToken } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    try {
      const user = await getCurrentUser({
        token: getToken() as string,
        setToken,
        orgId: orgId,
      });
      return { user };
    } catch (e) {
      console.log(e);
      return { user: null };
    }
  },
});

const formSchema = z.object({
  name: z
    .string()
    .min(3, { error: () => i18n.t("settings.organization.validation.nameMin") }),
  currency: z.literal(["USD", "GBP", "EUR"], {
    error: () => i18n.t("settings.organization.validation.currency"),
  }),
});

type FormSchema = z.infer<typeof formSchema>;

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function OrganizationComponent() {
  const { t } = useTranslation();
  const { user } = Route.useLoaderData();
  const { getToken, setToken, handleUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.organization_name || "",
      currency: user?.currency as "USD" | "GBP" | "EUR",
    },
  });

  const watchedName = form.watch("name");
  const previewSlug = watchedName
    ? generateSlug(watchedName)
    : user?.slug || "";

  const mutation = useMutation({
    mutationFn: async (data: FormSchema) => {
      if (!user) throw new Error("User not found");

      return updateOrganization(
        getToken() as string,
        data.name,
        data.currency,
        user.organization_id,
      );
    },
    onSuccess: async (response, data) => {
      const token = response.headers.get("access-token");
      if (token) {
        setToken(token);
      }

      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      toast.success(t("settings.organization.updated"));
      form.reset();
      handleUser({
        id: user?.organization_id as string,
        name: data.name,
        slug: previewSlug,
      });
      // update the user, this will update current org
      // update the org

      const path = "../../../" + previewSlug + dashboardPath;
      navigate({ to: path });
    },
    onError: (error: ErrorResponse) => {
      if (error.status_code > 499) {
        toast.error(t("settings.organization.updateFailed"));
        return;
      }
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = async (data: FormSchema) => {
    setIsSubmitting(true);
    mutation.mutate(data);
  };

  useEffect(() => {
    if (mutation.isError) {
      const err = mutation.error as unknown as ErrorResponse;

      Object.entries(err.msg).map(([key, value]) => {
        if (key === "slug" || key === "name") {
          form.setError("name", { message: value });
        } else if (key === "currency") {
          form.setError(key, { message: value });
        }
      });
    }
  }, [mutation.isError]);

  if (!user) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t("settings.organization.loadFailed")}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.organization.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("settings.organization.name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("settings.organization.namePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
                  {t("settings.organization.slug")}
                </label>
                <ReadOnlyInput>{previewSlug}</ReadOnlyInput>
              </div>

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("settings.organization.currency")}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("settings.organization.currencyPlaceholder")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="USD">
                          {t("settings.organization.currencies.USD")}
                        </SelectItem>
                        <SelectItem value="GBP">
                          {t("settings.organization.currencies.GBP")}
                        </SelectItem>
                        <SelectItem value="EUR">
                          {t("settings.organization.currencies.EUR")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? t("common.updating")
                    : t("settings.organization.submit")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <OneOffItemsSetting />

      <Card>
        <CardHeader>
          <CardTitle>{t("settings.organization.currentTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("settings.organization.currentName")}
            </span>
            <span className="font-medium">
              {user.organization_name || t("common.notSet")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("settings.organization.currentCurrency")}
            </span>
            <span className="font-medium">
              {user.currency || t("common.notSet")}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t("settings.organization.connectStores.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground text-sm">
            {t("settings.organization.connectStores.description")}
          </p>
          <ConnectStoresDialog />
        </CardContent>
      </Card>
    </div>
  );
}
